<template>
  <div class="modal fade" :id="id" tabindex="-1">
    <div class="modal-dialog">
      <form @submit.prevent="submitForm" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('steps.update_step')}}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
                  <span class="svg-icon svg-icon-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" 
                            fill="currentColor" />
                      <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                            fill="currentColor" />
                    </svg>
                  </span>
            </div>
          </div>
          <div class="modal-body">   
            <RequiredText />

            <div class="row mb-5">
              <div class="col-10 ">
                <Input
                  container-class="form-group"
                  :name="`title`+id"
                  type="text"
                  :label="$t('steps.title')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :isRequired="true" 
                  v-model="formData.title"
                />
              </div>
              <div class="col-2">
                <label class="form-label fs-6 text-black">
                  {{$t('steps.color')}} <span class="text-danger">*</span>
                </label>
                <div >
                  <ColorPicker 
                    v-model:pureColor="formData.color" 
                    format="hex"
                  />
                </div>
              </div>
            </div>
            <div class="row mb-8">
              <div class="col-12 col-md-12 col-lg-12">
                <div class="d-flex flex-column fv-row">
                  <label class="form-label fs-6 text-black mb-2">
                    {{ $t('taxes.percent') }} <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input type="number" v-model="formData.milestone_categories_progress" @keypress="onlyNumber" step=".01" class="form-control form-control-solid"
                      required :placeholder="$t('taxes.percent_placeholder')" />
                    <span class="input-group-text" id="basic-addon2">%</span>
                  </div>
                </div>
              </div>
            </div>
            <Input
                container-class="form-group col-12 col-md-12 mt-5 "
                :name="`project-type-delay_value`+id"
                type="number"
                labelClass="form-label fs-6 text-black"
                :label="$t('projectTypes.label.delay')"
                :inputClass="'form-control form-control-lg form-control-solid'"
                :isRequired="false"
                size="lg"
                :placeholder="''"
                v-model="formData.delay_value"
                :IsInputGroup="true"
                :min="1">
              <template #rightAddon>
                <Select2
                  container-class="form-group "
                    :multiple="false"
                    :show-search="false"
                    :parent-id="`#${id}`"
                    label-class="form-label fs-6 text-black"
                    :selectClass="'form-select form-control-sm form-select-solid rounded-start-0'"
                    :name="`projectTypes-delay-unit-`+id"
                    :isRequired="false"
                    v-model="formData.delay_unit">
                  <option v-for="(frequency, index) in recurringFrequencyEnum" :value="frequency.value" :key="index">
                    {{ frequency[locale] }}
                  </option>
                </Select2>
              </template>
            </Input>  
            <div class="row">
              <div class="col-12 col-md-12 col-lg-12 mt-5">
                <Select2
                  :parent-id="'#'+id"
                  :is-required="true"
                  container-class="form-group"
                  :multiple="true"
                  :show-search="true"
                  :name="'project_status'+id"
                  :label="$t('steps.choose_status')"
                  label-class="form-label fs-6 text-black"
                  v-model="formData.status"
              >
                <option v-for="status in getStatusData" :value="status?.id" :key="status?.id">
                    {{ status.title }}
                </option>
              </Select2>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
            <Button btn-class="btn-sm me-3" type="submit" id="submit_btn" btn-text="button.save" size="7px 30px" :is-loading="isLoading"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
   import { ref, defineEmits, defineProps, watch, onMounted } from "vue";
  import Input from "@/components/Input.vue";
  import Button from "@/components/Button.vue";
  import RequiredText from "@/components/RequiredText.vue";
  import { useStore } from "vuex";
  import { ColorPicker } from "vue3-colorpicker";
  import "vue3-colorpicker/style.css";
  import { computed } from "vue";
  import Select2 from "@/components/Select2.vue";
  import {useLocales} from "@/composables/locales";
  import VueNumberFormat from 'vue-number-format'

  const emits = defineEmits(["load"]);

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    form: {
      type: Object,
      default: () => ({
        id: "",
        title: "",
        milestone_category_id: "",
        color: "#000",
        status: [],
        milestone_categories_progress: 0,
        default: false,
        project_id: "",
        delay_value: 1,
        delay_unit: ''
      }),
    }
  });

  const store = useStore();
  const isLoading = ref(false);
  const formData = ref({ ...props.form });
  const {locale} = useLocales()

  const currentSociety = computed(() => store.getters['offices/getSelectedCompany'])
  const getStatusData = computed(() => store.getters['taskStatus/getTaskStatus'])
  const recurringFrequencyEnum = computed(() => store.getters["configs/getFrequencyEnum"])

  watch(
    () => props.form,
    (newForm) => {
      formData.value = { ...newForm };
    },
    { deep: true }
  );

  onMounted(() => {
    store.dispatch("taskStatus/loadTaskStatus")
    store.dispatch('configs/loadFrequencyEnum')
    store.dispatch('milestoneCategories/loadTaskMilestones')
  })

  function onlyNumber($event) {
    let keyCode = ($event.keyCode ? $event.keyCode : $event.which);
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { 
      $event.preventDefault();
    }
  }

  function closeModal() {
    $("#" + props.id).modal("toggle");
    formData.value.id = "";
    formData.value.title = "";
    formData.value.color = "#000";
    formData.value.status = [];
    formData.value.milestone_categories_progress = 0;
    formData.value.default = false;
    formData.value.delay_value = 1;
    formData.value.delay_unit= '';
  }

  function submitForm() {
    isLoading.value = true;
    store
      .dispatch("projectSteps/updateProjectStep", {...formData.value, society_id: currentSociety?.value?.id})
      .then(() => {
        isLoading.value = false;
        emits("load");
        closeModal();

        window.toastr["success"](i18n.global.t("notification.step_updated"));
      })
      .catch(() => {
        isLoading.value = false;
      });
  }
</script>

<style scoped>
  .color-picker-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }
</style>
